"use client";

import Box from "@mui/material/Box";
import { styled } from "@mui/material/styles";
import { parseVideoEmbed } from "./videoEmbedParser";

export interface VideoEmbedProps {
  /** Raw video URL (currently YouTube only — see videoEmbedParser.ts). */
  videoUrl?: string | null;
  /** Accessible iframe title. Defaults to "Embedded video". */
  title?: string;
  /** Optional data-testid override (defaults to "video-embed"). */
  testId?: string;
}

/**
 * Renders a responsive 16:9 YouTube iframe from a raw URL.
 *
 * Returns null if the URL is missing or unparseable, so callers can drop
 * it into their layout unconditionally:
 *
 *     <VideoEmbed videoUrl={config.videoUrl} />
 *
 * Uses the `youtube-nocookie.com` embed domain (no third-party cookies
 * until the user presses play — GDPR-friendlier and less likely to be
 * blocked by consent managers).
 */
export const VideoEmbed = ({
  videoUrl,
  title = "Embedded video",
  testId = "video-embed",
}: VideoEmbedProps) => {
  const parsed = videoUrl ? parseVideoEmbed(videoUrl) : null;
  if (!parsed) return null;

  return (
    <Wrapper data-testid={testId}>
      <iframe
        src={parsed.embedUrl}
        title={title}
        loading="lazy"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; fullscreen; gyroscope; picture-in-picture"
        allowFullScreen
      />
    </Wrapper>
  );
};

const Wrapper = styled(Box)`
  width: 100%;
  max-width: 480px;
  aspect-ratio: 16 / 9;
  border-radius: 12px;
  overflow: hidden;
  background: #000;

  iframe {
    width: 100%;
    height: 100%;
    border: 0;
    display: block;
  }
`;
